import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import type { Game, League } from '../GameLeagueSelector';
import {
  getLeaguePurgeOptions,
  getLeaguePurgeStatus,
  startLeaguePurge,
  type LeaguePurgeOption,
  type LeaguePurgeStatus,
} from '../../services/adminService';

interface LeagueCleanupDialogProps {
  open: boolean;
  onClose: () => void;
}

const LeagueCleanupDialog: React.FC<LeagueCleanupDialogProps> = ({ open, onClose }) => {
  const [games, setGames] = useState<Game[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedGame, setSelectedGame] = useState('');
  const [selectedLeague, setSelectedLeague] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [startingCleanup, setStartingCleanup] = useState(false);
  const [jobStatus, setJobStatus] = useState<LeaguePurgeStatus | null>(null);

  const availableLeagues = useMemo(() => {
    const gameId = games.find(game => game.code === selectedGame)?.id;
    return leagues.filter(league => league.gameId === gameId);
  }, [games, leagues, selectedGame]);

  const isCleanupRunning = jobStatus?.status === 'pending' || jobStatus?.status === 'running';
  const canStartCleanup = Boolean(selectedGame && selectedLeague && !loadingOptions && !startingCleanup && !isCleanupRunning);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    setOptionsError(null);

    try {
      const options = await getLeaguePurgeOptions();
      const { games: fetchedGames, leagues: fetchedLeagues } = mapCleanupOptions(options);
      setGames(fetchedGames);
      setLeagues(fetchedLeagues);

      const nextGame = fetchedGames.find(game => game.code === selectedGame)?.code ?? fetchedGames[0]?.code ?? '';
      const nextGameId = fetchedGames.find(game => game.code === nextGame)?.id;
      const nextAvailableLeagues = fetchedLeagues.filter(league => league.gameId === nextGameId);
      const nextLeague = nextAvailableLeagues.find(league => league.code === selectedLeague)?.code ?? nextAvailableLeagues[0]?.code ?? '';

      setSelectedGame(nextGame);
      setSelectedLeague(nextLeague);
    } catch (error: unknown) {
      setOptionsError(error instanceof Error ? error.message : 'Failed to load game and league options');
      setGames([]);
      setLeagues([]);
      setSelectedGame('');
      setSelectedLeague('');
    } finally {
      setLoadingOptions(false);
    }
  }, [selectedGame, selectedLeague]);

  useEffect(() => {
    if (!open) {
      setRequestError(null);
      setJobStatus(null);
      setStartingCleanup(false);
      return;
    }

    void loadOptions();
  }, [loadOptions, open]);

  useEffect(() => {
    if (availableLeagues.length === 0) {
      setSelectedLeague('');
      return;
    }

    const currentLeagueIsValid = availableLeagues.some(league => league.code === selectedLeague);
    if (!currentLeagueIsValid) {
      setSelectedLeague(availableLeagues[0].code);
    }
  }, [availableLeagues, selectedLeague]);

  useEffect(() => {
    if (!open || !jobStatus?.jobId || !isCleanupRunning) {
      return;
    }

    const intervalId = window.setInterval(async () => {
      try {
        const nextStatus = await getLeaguePurgeStatus(jobStatus.jobId);
        setJobStatus(nextStatus);

        if (nextStatus.status === 'completed') {
          await loadOptions();
        }
      } catch (error: unknown) {
        setJobStatus(currentStatus =>
          currentStatus
            ? {
                ...currentStatus,
                status: 'failed',
                error: error instanceof Error ? error.message : 'Failed to refresh cleanup progress',
              }
            : null,
        );
      }
    }, 1500);

    return () => window.clearInterval(intervalId);
  }, [isCleanupRunning, jobStatus?.jobId, loadOptions, open]);

  const handleDialogClose = () => {
    if (isCleanupRunning || startingCleanup) {
      return;
    }

    onClose();
  };

  const handleStartCleanup = async () => {
    if (!canStartCleanup) {
      return;
    }

    setStartingCleanup(true);
    setRequestError(null);

    try {
      const status = await startLeaguePurge({ game: selectedGame, keepLeague: selectedLeague });
      setJobStatus(status);
    } catch (error: unknown) {
      setRequestError(error instanceof Error ? error.message : 'Failed to start cleanup');
    } finally {
      setStartingCleanup(false);
    }
  };

  const selectedGameName = games.find(game => game.code === selectedGame)?.name ?? selectedGame;
  const selectedLeagueName = availableLeagues.find(league => league.code === selectedLeague)?.name ?? selectedLeague;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={isCleanupRunning}
      fullWidth
      maxWidth='md'
      aria-labelledby='league-cleanup-dialog-title'
    >
      <DialogTitle id='league-cleanup-dialog-title'>Cleanup: Keep One League</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Alert severity='error' icon={<WarningAmberIcon />}>
            This permanently deletes all data for every other league in the selected game, including characters, item history, events, items,
            exchange rates, divine orb history, and orphaned game accounts.
          </Alert>

          {optionsError && <Alert severity='error'>{optionsError}</Alert>}
          {requestError && <Alert severity='error'>{requestError}</Alert>}
          {jobStatus?.error && jobStatus.status === 'failed' && <Alert severity='error'>{jobStatus.error}</Alert>}
          {jobStatus?.status === 'completed' && (
            <Alert severity='success'>Cleanup completed successfully for {jobStatus.scope.game}. You can close this dialog now.</Alert>
          )}

          {loadingOptions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth disabled={isCleanupRunning || startingCleanup || games.length === 0}>
                <InputLabel id='cleanup-game-select-label'>Game to purge</InputLabel>
                <Select
                  labelId='cleanup-game-select-label'
                  value={selectedGame}
                  label='Game to purge'
                  onChange={event => setSelectedGame(event.target.value)}
                >
                  {games.map(game => (
                    <MenuItem key={game.id} value={game.code}>
                      {game.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth disabled={isCleanupRunning || startingCleanup || availableLeagues.length === 0}>
                <InputLabel id='cleanup-league-select-label'>League to keep</InputLabel>
                <Select
                  labelId='cleanup-league-select-label'
                  value={selectedLeague}
                  label='League to keep'
                  onChange={event => setSelectedLeague(event.target.value)}
                >
                  {availableLeagues.map(league => (
                    <MenuItem key={league.id} value={league.code}>
                      {league.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box>
            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
              Selected cleanup target
            </Typography>
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
              <Chip label={`Game: ${selectedGameName || 'Not selected'}`} color='primary' variant='outlined' />
              <Chip label={`Keep: ${selectedLeagueName || 'Not selected'}`} color='success' variant='outlined' />
            </Stack>
          </Box>

          {jobStatus && (
            <Box>
              <Typography variant='subtitle1' sx={{ mb: 1 }}>
                Cleanup progress
              </Typography>
              <LinearProgress variant='determinate' value={jobStatus.progressPercent} sx={{ height: 10, borderRadius: 999, mb: 1.5 }} />
              <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                {jobStatus.phase}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {jobStatus.processedWorkItems} / {jobStatus.totalWorkItems} work items processed ({jobStatus.progressPercent}%)
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mt: 2 }}>
                <Chip label={`Characters: ${jobStatus.deletedCounts.characters}`} variant='outlined' />
                <Chip label={`History: ${jobStatus.deletedCounts.characterItemHistory}`} variant='outlined' />
                <Chip label={`Events: ${jobStatus.deletedCounts.events}`} variant='outlined' />
                <Chip label={`Items: ${jobStatus.deletedCounts.items}`} variant='outlined' />
                <Chip label={`Rates: ${jobStatus.deletedCounts.itemExchangeRates}`} variant='outlined' />
                <Chip label={`Divine history: ${jobStatus.deletedCounts.divineOrbHistory}`} variant='outlined' />
                <Chip label={`Game accounts: ${jobStatus.deletedCounts.gameAccounts}`} variant='outlined' />
                <Chip label={`Leagues purged: ${jobStatus.scope.targetLeagueCount}`} variant='outlined' />
              </Box>

              {jobStatus.scope.targetLeagues.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Target leagues: {jobStatus.scope.targetLeagues.join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={isCleanupRunning || startingCleanup}>
          {jobStatus?.status === 'completed' ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={handleStartCleanup}
          disabled={!canStartCleanup}
          startIcon={startingCleanup ? <CircularProgress size={18} color='inherit' /> : <DeleteSweepIcon />}
        >
          {startingCleanup ? 'Starting cleanup...' : 'Delete All Other Leagues'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeagueCleanupDialog;

const mapCleanupOptions = (options: LeaguePurgeOption[]): { games: Game[]; leagues: League[] } => {
  const gameMap = new Map<string, Game>();
  const leagues: League[] = [];

  options.forEach((option, index) => {
    if (!gameMap.has(option.game)) {
      gameMap.set(option.game, {
        id: option.game,
        code: option.game,
        name: option.game === 'PoE1' ? 'Path of Exile 1' : 'Path of Exile 2',
        isActive: true,
      });
    }

    leagues.push({
      id: `${option.game}:${option.league}:${index}`,
      code: option.league,
      name: option.league.charAt(0).toUpperCase() + option.league.slice(1).toLowerCase(),
      gameId: option.game,
      isActive: true,
    });
  });

  return {
    games: Array.from(gameMap.values()),
    leagues,
  };
};